import React, {useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {login} from "../firebase/auth";
import RequestAccessForm from "../components/RequestAccessForm";
import LoginShell, {LOGIN_COLORS} from "./LoginShell";
import {consumeReturnUrl} from "./loginReturnUrl";


const AuthPage = ({alert, onSwitchToDemo}) => {
    const [activeTab, setActiveTab] = useState('login');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);


    const [error, setError] = useState({email: null, password: null});
    const [loading, setLoading] = useState(false);

    
    return (
        <LoginShell alert={alert}>
            <Stack spacing={2}>
                {activeTab === 'login' && (
                    <Stack spacing={2}>
                        <Typography
                            sx={{
                                fontSize: 24,
                                fontWeight: 700,
                                color: LOGIN_COLORS.slate900,
                                mb: 0.5,
                            }}
                        >
                            Sign In
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: LOGIN_COLORS.slate500,
                                mb: 2,
                            }}
                        >
                            Sign in with your credentials to access DASH.
                        </Typography>

                        {error?.wrong_credentials && (
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: '1px solid #fecaca',
                                    bgcolor: '#fef2f2',
                                    px: 2,
                                    py: 1.25,
                                    fontSize: 14,
                                    color: '#b91c1c',
                                }}
                            >
                                Incorrect email or password.
                            </Box>
                        )}

                        {error?.email && !error?.wrong_credentials && (
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: '1px solid #fecaca',
                                    bgcolor: '#fef2f2',
                                    px: 2,
                                    py: 1.25,
                                    fontSize: 14,
                                    color: '#b91c1c',
                                }}
                            >
                                {error.email}
                            </Box>
                        )}

                        <Box>
                            <Typography
                                sx={{
                                    mb: 0.75,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: LOGIN_COLORS.slate500,
                                }}
                            >
                                Email
                            </Typography>
                            <TextField
                                autoFocus={true}
                                fullWidth
                                error={!!error?.email}
                                variant="outlined"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (error?.email || error?.wrong_credentials) {
                                        setError({});
                                    }
                                }}
                                autoComplete="username"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        fontSize: 14,
                                        '& fieldset': {
                                            borderColor: LOGIN_COLORS.slate200,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: LOGIN_COLORS.slate200,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: LOGIN_COLORS.brand,
                                            borderWidth: 2,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        display: 'none',
                                    },
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    mb: 0.75,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: LOGIN_COLORS.slate500,
                                }}
                            >
                                Password
                            </Typography>
                            <TextField
                                fullWidth
                                error={!!error?.password}
                                variant="outlined"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (error?.password || error?.wrong_credentials) {
                                        setError({});
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleLogin();
                                }}
                                autoComplete="current-password"
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                size="small"
                                            >
                                                <VisibilityOutlinedIcon fontSize="small"/>
                                            </IconButton>
                                        )
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        fontSize: 14,
                                        '& fieldset': {
                                            borderColor: LOGIN_COLORS.slate200,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: LOGIN_COLORS.slate200,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: LOGIN_COLORS.brand,
                                            borderWidth: 2,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        display: 'none',
                                    },
                                }}
                            />
                        </Box>

                        <Stack direction="row" justifyContent={'flex-start'} alignItems={'center'}>
                            <FormControlLabel
                                control={<Checkbox defaultChecked size="small"/>}
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        Keep me logged in
                                    </Typography>
                                }
                            />
                        </Stack>

                        <Button
                            fullWidth
                            size="large"
                            disabled={loading}
                            onClick={handleLogin}
                            sx={{
                                mt: 1,
                                borderRadius: 2,
                                bgcolor: LOGIN_COLORS.slate900,
                                color: 'white',
                                py: 1.25,
                                fontSize: 14,
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: '#334155',
                                },
                                '&.Mui-disabled': {
                                    opacity: 0.6,
                                    color: 'white',
                                },
                            }}
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </Button>

                        <Divider sx={{my: 1}}>
                            <Typography variant="body2" color="text.secondary">
                                OR
                            </Typography>
                        </Divider>

                        <Button
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                color: 'text.secondary',
                            }}
                            onClick={() => {
                                setActiveTab('requestAccess');
                                setError(null);
                            }}
                        >
                            Request Access
                        </Button>

                        {onSwitchToDemo && (
                            <>
                                <Divider sx={{my: 1}}>
                                    <Typography variant="body2" color="text.secondary">
                                        OR
                                    </Typography>
                                </Divider>

                                <Button
                                    fullWidth
                                    sx={{
                                        textTransform: 'none',
                                        color: 'text.secondary',
                                    }}
                                    onClick={onSwitchToDemo}
                                >
                                    Switch to Demo Login
                                </Button>
                            </>
                        )}
                    </Stack>
                )}
                {activeTab === 'requestAccess' && (
                    <Stack spacing={2}>
                        <Typography
                            sx={{
                                fontSize: 24,
                                fontWeight: 700,
                                color: LOGIN_COLORS.slate900,
                                mb: 0.5,
                            }}
                        >
                            Request Access
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: LOGIN_COLORS.slate500,
                                mb: 2,
                            }}
                        >
                            Enter your email to request access to DASH.
                        </Typography>

                        <RequestAccessForm embedded={true}/>

                        <Button
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                color: 'text.secondary',
                                mt: 1,
                            }}
                            onClick={() => setActiveTab('login')}
                        >
                            Back to Login
                        </Button>
                    </Stack>
                )}
            </Stack>
        </LoginShell>
    );

    function validateEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    function validatePassword(password) {
        return password.length >= 6;
    }

    async function handleLogin() {
        let errors = {};

        if (!validateEmail(email)) {
            errors.email = 'Invalid email address';
        }
        if (!validatePassword(password)) {
            errors.password = 'Password must be at least 6 characters long';
        }
        if (Object.keys(errors).length > 0) {
            setError(prevState => ({...prevState, ...errors}));
            return;
        }

        setLoading(true);
        setError(null);
        try {
            await login({email, password});
            // If there's a saved return URL (from a logout redirect), go back there.
            const returnUrl = consumeReturnUrl();
            if (returnUrl) {
                window.location.replace(returnUrl);
                return;
            }
            // No return URL — UserProvider will detect auth state change
            // and App component will automatically route to the correct page based on role
        } catch (e) {
            console.error('Login error:', e);

            // Handle auth errors
            if (e.code === "auth/invalid-credential" || e.code === "auth/wrong-password") {
                setError({wrong_credentials: true});
            } else if (e.code === "auth/user-not-found") {
                setError({wrong_credentials: true});
            } else if (e.code === "auth/too-many-requests") {
                setError({email: 'Too many failed attempts. Please try again later.'});
            } else if (e.code === "auth/user-disabled") {
                setError({email: 'This account has been disabled.'});
            } else if (e.code === "auth/network-request-failed") {
                setError({email: 'Network error. Please check your connection.'});
            } else {
                setError({email: 'An error occurred. Please try again.'});
            }
        } finally {
            setLoading(false);
        }
    }
};

export default AuthPage;
